<template>
  <section
    class="AiEvents"
    :aria-label="$t('accessibility.description.hotelEventsList')">
    <div class="Layout EventsBlock">
      <article class="Layout-section">
        <grid-item sm="3" md="10" l="3" offset-md="1">
          <ai-typo
            class="AiPrivateEvents-title"
            as="h2"
            variant="heading-02"
            :aria-label="title">
            {{ title }}
          </ai-typo>
        </grid-item>
        <grid-item
          id="Events"
          sm="4"
          md="10"
          l="7"
          offset-md="1"
          offset-l="0"
          class="AiEvent-section"
          role="list">
          <ai-event
            v-for="(aiEvent, index) of entertainmentLoaded"
            :key="`AiPrivateEvent-item-${index}`"
            :end-date="aiEvent.endDate ?? ''"
            :event-description="aiEvent.description ?? ''"
            :event-title="aiEvent.title ?? ''"
            :location="location"
            :start-date="aiEvent.startDate ?? ''"
            :image="aiEvent.picture as AiPicture" />
          <div
            v-if="entertainments.length > BASE_ITEM_LENGTH"
            class="AiEvents-button--wrapper"
            :class="{
              'AiEvents-button--wrapper-noChevron': entertainments.length <= 4,
            }">
            <ai-button
              :class="[
                'AiPrivateEvent-button--chevron',
                { 'AiPrivateEvent-button--chevron-expanded': expanded },
              ]"
              :slim="true"
              :icon-right="'chevron' as IconName"
              :variant="AiButtonVariant.Tertiary"
              :label="buttonLabel"
              role="button"
              :aria-label="buttonLabel"
              :aria-expanded="ariaexpanded"
              aria-controls="Events"
              @click="toggleExpanded" />
          </div>
        </grid-item>
      </article>
    </div>
  </section>
</template>

<script setup lang="ts">
import AiTypo from '../../atoms/AiTypo/AiTypo.vue';
import AiEvent from '../../molecules/AiEvent/AiEvent.vue';
import AiButton from '../../atoms/AiButton/AiButton.vue';
import { AiButtonVariant } from '../../atoms/AiButton/constants';
import type { IconName } from '../../atoms/AiIcon/types';

import type { AiPicture, HotelEntertainment } from '~~/domains/graphql';

export interface AiEventsProps {
  title: string;
  location: string;
  entertainments?: HotelEntertainment[];
}

const BASE_ITEM_LENGTH = 2;

const props = withDefaults(defineProps<AiEventsProps>(), {
  entertainments: () => [],
});

const itemLength = ref<number>(BASE_ITEM_LENGTH);
const entertainmentLoaded = computed(() =>
  props.entertainments.slice(0, itemLength.value),
);
const { t } = useI18n();
const expanded = ref<boolean>(false);
const buttonLabel = computed<string>(() =>
  expanded.value ? t('hotel.events.buttonLess') : t('hotel.events.buttonMore'),
);
const ariaexpanded = ref<boolean>(false);
const toggleExpanded = () => {
  if (!expanded.value) {
    itemLength.value += 3;
    ariaexpanded.value = !ariaexpanded.value;
  } else {
    itemLength.value -= 3;
  }
  expanded.value = !expanded.value;
};
</script>
<style lang="scss">
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/mixins';
@use '@/assets/styles/utilities/grid';

.AiEvents {
  background-color: colors.$gold-100;
}

.EventsBlock {
  padding-top: constants.$padding-07;
  @include mixins.rem-fallback(padding-bottom, 72);
}

.AiPrivateEvents-title {
  @include mixins.rem-fallback(margin-bottom, 40);
}

.AiEvents-button--wrapper {
  display: flex;
  flex-direction: column;

  @media (mq.$from-medium) {
    flex-direction: row;
    justify-content: space-between;

    &-noChevron {
      justify-content: flex-end;
    }
  }
}
.AiPrivateEvent-button--chevron {
  text-align: start;
  margin-bottom: constants.$margin-02;
  svg {
    transform: rotate(180deg);
    fill: colors.$gold-900;
  }
  &-expanded {
    svg {
      transform: rotate(360deg);
    }
  }
}
</style>
